import {
  Box,
  Disclaimer,
  Div,
  Icon,
  lightTheme,
  P,
  Pill,
  PlusIcon,
  SupportTicket,
} from '@vgrubs/components';
import { formatTicketAge } from '../helpers/supportTicketLabels';
import { RowSubject } from './SupportTicketRow.styled';
import { TicketStatusPill } from './TicketStatusPill';
import { TicketTypeLabel } from './TicketTypeLabel';

type Props = {
  ticket: SupportTicket;
  onOpen: (ticketId: number) => void;
  /** Takes the ticket without opening it, the way a delivery is claimed. */
  onClaim: (ticketId: number) => void;
  /** This row's claim is in flight. */
  claiming?: boolean;
};

/**
 * One ticket in the list.
 *
 * The age is the number that matters and sits where the eye lands last: the
 * list is sorted oldest first inside each status, so a row reading "3d" at the
 * top of Open is the whole point of the page.
 */
export const SupportTicketRow = ({
  ticket,
  onOpen,
  onClaim,
  claiming,
}: Props) => (
  <Box $mb="8" $pointer onClick={() => onOpen(ticket.id)}>
    {/* Tighter once it stacks: on a phone the pills sit under the text
          rather than across from it, and the desktop gap reads as a gap
          between two rows. */}
    <Div
      $flex
      $between
      $alignCenter
      $gap="16px"
      $gapSM="8px"
      $columnSM
      $alignStartSM
    >
      {/* Full width once it stacks: the row aligns its children to the start
            on a phone, which sizes this column to its longest line - and a
            subject that cannot be measured against anything never ellipses, it
            just runs through the card's padding. */}
      <Div $flex $column $gap="4px" $widthSM="100%" style={{ minWidth: 0 }}>
        <Div $flex $alignCenter $gap="8px">
          <Disclaimer $m="0">#{ticket.id}</Disclaimer>
          <TicketTypeLabel type={ticket.type} />
        </Div>

        <RowSubject>{ticket.subject}</RowSubject>

        <Disclaimer $m="0">
          {[
            ticket.merchantName,
            ticket.locationName,
            ticket.order ? `Order #${ticket.order.id}` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Disclaimer>
      </Div>

      <Div $flex $alignCenter $gap="12px" $flexNone>
        {/* How long it has waited, in the same shape as the status beside
              it - the list is sorted by this, so it is a fact about the ticket
              rather than a note in the margin. */}
        <Pill $background={lightTheme.gray}>
          <Disclaimer
            $m="0"
            $color={lightTheme.darkGray}
            $semiBold
            $lineHeight="1"
          >
            {formatTicketAge(ticket.createdAt)}
          </Disclaimer>
        </Pill>
        <TicketStatusPill status={ticket.status} />

        {/* Taking a ticket is the one thing done from the list itself, so it
              sits here rather than behind the modal - the same control, in the
              same place, as claiming a delivery. The click must not also open
              the row. */}
        {ticket.status === 'open' && (
          <Div
            $flex
            $alignCenter
            $gap="4px"
            $pointer
            $opacity={claiming ? '0.6' : '1'}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              if (!claiming) onClaim(ticket.id);
            }}
          >
            <Icon $pointer $size={20}>
              <PlusIcon />
            </Icon>
            <P $bold $m="0" $color={lightTheme.primary}>
              Claim
            </P>
          </Div>
        )}

        {ticket.claimedBy && <P $m="0">{ticket.claimedBy}</P>}
      </Div>
    </Div>
  </Box>
);
